import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { trackError } from '@local/metrics';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext, useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { Navigate } from 'react-router';
import { useLoadLicenseOrgsQuery, useLoadLicenseTokenQuery, } from '../../../apiClients/entitlementsClient/requests/EntitlementsAPI';
import { LOGIN_ERROR_ROUTE, LOGIN_ROUTE } from '../../../constants';
import { getCombinedToken as getAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { setCurrentEvoInstance } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { Background } from '../../background/Background';
import { LoginContext } from '../../loginHandler/LoginContext';
import { OrgHubSelectMenu } from '../OrgHubSelectMenu/OrgHubSelectMenu';
import { useStyles } from '../sharedSelector.styles';
function evoOptionsGroups(data, evoMap) {
    const response = [];
    if (data) {
        [...data.organizations]
            .sort((a, b) => (a.display_name > b.display_name ? 1 : -1))
            .forEach((org) => {
            const hubs = [];
            for (const entry of evoMap.entries()) {
                if (entry[1].org.id === org.id) {
                    hubs.push(entry[1].hub);
                }
            }
            if (hubs.length > 0) {
                response.push({
                    label: org.display_name,
                    optionIds: hubs
                        .sort((a, b) => (a.display_name > b.display_name ? 1 : -1))
                        .map((hub) => `${org.id}-${hub.code}`),
                });
            }
        });
    }
    return response;
}
function generateEvoMap(data, service) {
    const map = new Map();
    if (data) {
        const hubs = new Map();
        for (const hub of data.hubs) {
            hubs.set(hub.code, hub);
        }
        const orgs = new Map();
        for (const org of data.organizations) {
            orgs.set(org.id, org);
        }
        for (const sa of data.service_access) {
            const hub = hubs.get(sa.hub_code);
            const org = orgs.get(sa.org_id);
            if (sa.services.includes(service) && hub && org) {
                map.set(`${sa.org_id}-${sa.hub_code}`, { org, hub });
            }
        }
    }
    return map;
}
function instances(data) {
    const items = [];
    for (const entry of data.entries()) {
        items.push({ key: entry[0], label: entry[1].hub.display_name });
    }
    return items.sort();
}
function getDecodedIDToken(accessToken) {
    if (accessToken && accessToken.id_token) {
        return decodeToken(accessToken.id_token);
    }
    return null;
}
export function OrgHubSelector({ homePagePath = '/' }) {
    var _a;
    const { service } = useContext(LoginContext);
    const evoData = useLoadLicenseOrgsQuery({ service });
    const { classes } = useStyles();
    const evoMap = generateEvoMap(evoData.data, service);
    const [selectedInstance, setSelectedInstance] = useState('');
    const [fetchToken, setFetchToken] = useState(false);
    const evoInstance = selectedInstance && evoMap.has(selectedInstance) ? evoMap.get(selectedInstance) : undefined;
    const evoToken = useLoadLicenseTokenQuery(fetchToken && evoInstance
        ? {
            service,
        }
        : skipToken);
    useEffect(() => {
        if (evoMap.size === 1 && !fetchToken) {
            const [key, instance] = [...evoMap.entries()][0];
            setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
            setSelectedInstance(key);
            setFetchToken(true);
        }
    }, [evoMap]);
    const accessToken = getAccessToken();
    const decodedToken = getDecodedIDToken(accessToken);
    const userName = decodedToken ? `${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.given_name} ${decodedToken === null || decodedToken === void 0 ? void 0 : decodedToken.family_name}` : '';
    const expired = accessToken && accessToken.expires_at <= Date.now();
    if (expired) {
        return _jsx(Navigate, { to: LOGIN_ROUTE });
    }
    if (evoData.error) {
        trackError('Error loading instances', `Error loading instances for ${service} in <OrgSelector>`, {
            evo: evoData.error,
        });
        return _jsx(Navigate, { to: `${LOGIN_ERROR_ROUTE}?error=entitlements_failure` });
    }
    if (evoToken.error) {
        trackError('Error loading evo token', `Error loading token for ${service} in <OrgSelector>`, {
            evoToken: evoToken.error,
        });
        return _jsx(Navigate, { to: `${LOGIN_ERROR_ROUTE}?error=entitlements_failure` });
    }
    if (evoData.isLoading || evoToken.isLoading) {
        return (_jsx(Background, { children: _jsx(Grid, { item: true, style: {
                    position: 'relative',
                    width: '100%',
                    height: '100px',
                }, alignContent: "center", justifyContent: "center", display: "grid", children: _jsx(CircularProgress, { "automation-id": "spinner-loading" }) }) }));
    }
    function selectInstance() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!selectedInstance) {
                return;
            }
            const instance = evoMap.get(selectedInstance);
            if (!instance) {
                return;
            }
            setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
            setFetchToken(true);
        });
    }
    if (((_a = evoData.data) === null || _a === void 0 ? void 0 : _a.service_access.length) === 0) {
        setCurrentEvoInstance({ hasNoLicense: true });
        return _jsx(Navigate, { to: homePagePath });
    }
    if (fetchToken && evoToken.data) {
        return _jsx(Navigate, { to: homePagePath });
    }
    const optionGroups = evoOptionsGroups(evoData.data, evoMap);
    const selected = evoData.data ? optionGroups[0].optionIds[0] : '';
    return (_jsx(Background, { children: _jsxs(Grid, { item: true, xs: 12, className: classes.formContainer, children: [_jsxs("div", { className: classes.label, children: [_jsxs(Typography, { children: ["Name: ", userName || null] }), _jsx(Grid, { item: true, xs: true, className: classes.selectMenu, children: _jsx(OrgHubSelectMenu, { options: instances(evoMap), optionGroups: optionGroups, onSelected: (key) => setSelectedInstance(key), defaultValue: selected, width: 360 }) })] }), _jsx(Button, { color: "primary", variant: "contained", size: "large", className: classes.button, onClick: () => selectInstance(), disabled: !selectedInstance, id: "continue-btn", children: "Continue" })] }) }));
}
//# sourceMappingURL=OrgHubSelector.js.map